import React, { useEffect, useState } from 'react';
import { Link as Anchor } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faHome, 
    faTruck, 
    faCogs, 
    faShieldAlt, 
    faChartLine,
    faCheckCircle,
    faClock,
    faUserTie,
    faTools,
    faHandsHelping,
    faQuoteLeft
} from '@fortawesome/free-solid-svg-icons';
import CountUp from 'react-countup';
import './HomePage.css';
import HeaderBack from '../../Components/HeaderBack/HeaderBack';

export default function HomePage() {
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        // Scroll suave al cargar en móvil
        if (window.innerWidth <= 768) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Efecto parallax
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };
        
        window.addEventListener('scroll', handleScroll);

        // Carga optimizada de imágenes
        const lazyImages = [].slice.call(document.querySelectorAll("img.lazy-load"));
        
        if ("IntersectionObserver" in window) {
            const lazyImageObserver = new IntersectionObserver(function(entries, observer) {
                entries.forEach(function(entry) {
                    if (entry.isIntersecting) {
                        const lazyImage = entry.target;
                        lazyImage.src = lazyImage.dataset.src;
                        lazyImage.classList.remove("lazy-load");
                        lazyImage.classList.add("loaded");
                        lazyImageObserver.unobserve(lazyImage);
                    }
                });
            }, {
                rootMargin: '200px 0px'
            });

            lazyImages.forEach(function(lazyImage) {
                lazyImageObserver.observe(lazyImage);
            });
        } else {
            // Fallback para navegadores que no soportan IntersectionObserver
            lazyImages.forEach(function(lazyImage) {
                lazyImage.src = lazyImage.dataset.src;
                lazyImage.classList.remove("lazy-load");
                lazyImage.classList.add("loaded");
            });
        }

        // Marcamos que las imágenes han comenzado a cargar
        setImagesLoaded(true);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <div className="nosotros-page">
            {/* Encabezado con imagen de fondo */}
            <div className='bgPage' style={{ 
                backgroundImage: imagesLoaded ? 
                    `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(/images/dumpers.png)` : 
                    'none',
                backgroundColor: imagesLoaded ? 'transparent' : 'var(--color1)'
            }}>
                <HeaderBack title='TechMiningOne' />
                <div className="breadcrumb">
                    <Anchor to="/">
                        <FontAwesomeIcon icon={faHome} /> Inicio
                    </Anchor>
                    <span>|</span>
                    <Anchor to=''>
                        Home
                    </Anchor>
                </div>
            </div>

            <div className="ProductsContainPage">
                <div className="nosotros-container">
                    {/* Sección Hero */}
                    <section className="hero-section">
                        <h1>Soluciones Integrales para la Minería</h1>
                        <p className="lead-text">Expertos en dumpers, repuestos y servicios de mantenimiento con garantía de calidad</p>
                    </section>

                    {/* Galería de productos */}
                    <section className="info-section product-gallery-section">
                        <div className="section-header">
                            <FontAwesomeIcon icon={faTruck} className="section-icon" />
                            <h2>Nuestros Productos en Acción</h2>
                        </div>
                        <div className="products-gallery">
                            <div className="product-card">
                                <div className="image-container">
                                    <img 
                                        data-src="/images/dumper1.jpg" 
                                        alt="Dumper en operación minera" 
                                        className="product-image lazy-load"
                                        style={{ transform: `translateY(${scrollY * 0.03}px)` }} 
                                    />
                                </div>
                                <div className="product-overlay">
                                    <h3>Dumpers de Alto Rendimiento</h3>
                                    <p>Modelos para todas las necesidades mineras</p>
                                </div>
                            </div>
                            <div className="product-card">
                                <div className="image-container">
                                    <img 
                                        data-src="/images/repuestos.jpg" 
                                        alt="Repuestos originales" 
                                        className="product-image lazy-load"
                                        style={{ transform: `translateY(${scrollY * 0.02}px)` }} 
                                    />
                                </div>
                                <div className="product-overlay">
                                    <h3>Repuestos 100% Originales</h3>
                                    <p>Garantía de autenticidad y durabilidad</p>
                                </div>
                            </div>
                            <div className="product-card">
                                <div className="image-container">
                                    <img 
                                        data-src="/images/mantenimiento.jpg" 
                                        alt="Servicio de mantenimiento" 
                                        className="product-image lazy-load"
                                        style={{ transform: `translateY(${scrollY * 0.04}px)` }} 
                                    />
                                </div>
                                <div className="product-overlay">
                                    <h3>Mantenimiento Especializado</h3>
                                    <p>Técnicos certificados por fabricantes</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Sección Estadísticas */}
                    <section className="info-section stats-section">
                        <div className="section-header">
                            <FontAwesomeIcon icon={faChartLine} className="section-icon" />
                            <h2>Nuestra Experiencia en Cifras</h2>
                        </div>
                        <div className="stats-grid">
                            <div className="stat-card">
                                <h3><CountUp end={150} duration={2} />+</h3>
                                <p>Equipos vendidos</p>
                            </div>
                            <div className="stat-card">
                                <h3><CountUp end={5000} duration={2} />+</h3>
                                <p>Repuestos disponibles</p>
                            </div>
                            <div className="stat-card">
                                <h3><CountUp end={98} duration={2} suffix="%"/></h3>
                                <p>Satisfacción clientes</p>
                            </div>
                        </div>
                    </section>

                    {/* Testimonios reales con fotos */}
                    <section className="info-section testimonials-section">
                        <div className="section-header">
                            <FontAwesomeIcon icon={faQuoteLeft} className="section-icon" />
                            <h2>Lo que dicen nuestros clientes</h2>
                        </div>
                        <div className="testimonials-grid">
                            <div className="testimonial-card">
                                <div className="testimonial-image-container">
                                    <img 
                                        data-src="/images/cliente1.jpg" 
                                        alt="Cliente satisfecho" 
                                        className="testimonial-image lazy-load" 
                                    />
                                </div>
                                <div className="testimonial-content">
                                    <FontAwesomeIcon icon={faQuoteLeft} className="quote-icon" />
                                    <p>"Tech Mining One nos proporcionó dumpers que aumentaron nuestra productividad en un 30%. Su servicio postventa es excepcional."</p>
                                    <div className="client-info">
                                        <strong>Juan Pérez</strong>
                                        <span>Gerente de Operaciones, Minera Andina</span>
                                    </div>
                                </div>
                            </div>
                            <div className="testimonial-card">
                                <div className="testimonial-image-container">
                                    <img 
                                        data-src="/images/cliente2.jpg" 
                                        alt="Cliente satisfecho" 
                                        className="testimonial-image lazy-load" 
                                    />
                                </div>
                                <div className="testimonial-content">
                                    <FontAwesomeIcon icon={faQuoteLeft} className="quote-icon" />
                                    <p>"Los repuestos llegaron en tiempo récord cuando tuvimos una emergencia. Salvaron nuestra producción."</p>
                                    <div className="client-info">
                                        <strong>María Gómez</strong>
                                        <span>Superintendente de Mantenimiento</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Nuestro equipo técnico */}
                    <section className="info-section team-section">
                        <div className="section-header">
                            <FontAwesomeIcon icon={faUserTie} className="section-icon" />
                            <h2>Conoce a Nuestro Equipo</h2>
                        </div>
                        <div className="team-grid">
                            <div className="team-image-container">
                                <img 
                                    data-src="/images/equipo2.jpg" 
                                    alt="Equipo técnico" 
                                    className="team-image lazy-load"
                                    style={{ transform: `translateY(${scrollY * 0.02}px)` }} 
                                />
                            </div>
                            <div className="team-details">
                                <h3>Expertos en Minería</h3>
                                <p>Nuestro equipo combina experiencia práctica con conocimiento técnico avanzado para ofrecer soluciones efectivas.</p>
                                <ul>
                                    <li>Certificaciones OEM</li>
                                    <li>+10 años experiencia promedio</li>
                                    <li>Disponibilidad 24/7</li>
                                </ul>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}